import React from 'react';
import { Link } from 'react-router-dom';
import { Video, ArrowLeft, Terminal, Code } from 'lucide-react';

export const YouTubeSearch: React.FC = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/elevation/lab"
          className="flex items-center text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Lab
        </Link>
      </div>

      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <div className="flex items-center space-x-4 mb-6">
          <div className="p-4 bg-consulting-slate-blue-50 border border-consulting-slate-blue-200">
            <Video className="h-8 w-8 text-consulting-slate-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">YouTube Prioritization Search</h1>
            <p className="text-charcoal-600 mt-1">Search YouTube videos and transcripts for prioritization-related content</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-charcoal-900 mb-3">Overview</h2>
            <p className="text-charcoal-700 leading-relaxed">
              The YouTube Prioritization Search is integrated into the Prospector Engine and allows you to search 
              YouTube videos for prioritization-related content. It can search video transcripts for keywords and 
              find relevant content across the platform.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-charcoal-900 mb-3 flex items-center">
              <Code className="h-5 w-5 mr-2 text-consulting-slate-blue-600" />
              API Endpoints
            </h2>
            <div className="space-y-4">
              <div className="bg-[var(--surface)] p-4 border border-[var(--rule)]">
                <h3 className="font-semibold text-charcoal-900 mb-2">Search Videos</h3>
                <code className="block p-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)] font-mono text-sm mb-2">
                  GET http://localhost:4001/api/youtube/search?query=your+query&maxResults=50
                </code>
                <p className="text-sm text-charcoal-600">Returns: List of videos matching the search query</p>
              </div>

              <div className="bg-[var(--surface)] p-4 border border-[var(--rule)]">
                <h3 className="font-semibold text-charcoal-900 mb-2">Search Transcripts</h3>
                <code className="block p-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)] font-mono text-sm mb-2">
                  GET http://localhost:4001/api/youtube/transcript-search?query=your+query&keyword=keyword&maxVideos=10
                </code>
                <p className="text-sm text-charcoal-600">Returns: Videos with transcript matches for the keyword</p>
              </div>

              <div className="bg-[var(--surface)] p-4 border border-[var(--rule)]">
                <h3 className="font-semibold text-charcoal-900 mb-2">Get Video Transcript</h3>
                <code className="block p-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)] font-mono text-sm mb-2">
                  GET http://localhost:4001/api/youtube/transcript/:videoId
                </code>
                <p className="text-sm text-charcoal-600">Returns: Full transcript for a specific video</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-charcoal-900 mb-3 flex items-center">
              <Terminal className="h-5 w-5 mr-2 text-consulting-slate-blue-600" />
              Usage Example
            </h2>
            <div className="bg-[var(--surface)] p-4 border border-[var(--rule)]">
              <pre className="text-sm text-charcoal-700 whitespace-pre-wrap">
{`# Search for videos about prioritization
curl "http://localhost:4001/api/youtube/search?query=prioritization+podcast&maxResults=10"

# Search transcripts for keyword
curl "http://localhost:4001/api/youtube/transcript-search?query=prioritization+podcast&keyword=prioritization&maxVideos=5"

# Get specific video transcript
curl "http://localhost:4001/api/youtube/transcript/dQw4w9WgXcQ"`}
              </pre>
            </div>
          </div>

          <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
            <h3 className="font-semibold text-[var(--mustard)] mb-2">Configuration</h3>
            <p className="text-sm text-[var(--mustard)] mb-2">
              Set the following environment variable in the Prospector Engine:
            </p>
            <code className="block p-2 bg-[var(--bg-raised)] rounded border border-[var(--mustard)] font-mono text-sm">
              YOUTUBE_API_KEY=your-youtube-api-key-here
            </code>
          </div>

          <div className="bg-consulting-slate-blue-50 p-4 border border-consulting-slate-blue-200">
            <h3 className="font-semibold text-consulting-slate-blue-900 mb-2">Location</h3>
            <p className="text-sm text-consulting-slate-blue-800">
              <code className="bg-[var(--bg-raised)] px-1 rounded">engines/prospector-engine/src/services/youtubeService.js</code>
            </p>
            <p className="text-sm text-consulting-slate-blue-800 mt-2">
              <code className="bg-[var(--bg-raised)] px-1 rounded">engines/prospector-engine/src/routes/youtube.js</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};





